import { UnitStack } from '@/view/renderers/UnitStack';
import { FactionType, Hex, TerrainType } from '@/core/types/battle.types';
import { axialToPixel, coordsToId } from '@/core/utils/hex.utils';
import { useBattleStore } from '@/store/useBattleStore';
import * as PIXI from 'pixi.js';
import { PathCalculator } from '@/core/battle/PathCalculator';
import { TERRAINS } from '@/core/constants/battle.constants';
import { UnitId, UNITS_DATA } from '@/data/units';
import ManagerRegistry from '@/core/battle/ManagerRegistry';
import { resolveAsset } from '@/core/utils/common.utils';



export class UnitFactory {

    static async createUnit(
        uniqueUnitId: string,
        unitId: UnitId, 
        spawnZone: { front: {q: number, r: number}, back: {q: number, r: number} },
        count: number, 
        faction: FactionType = FactionType.Player,
    ): Promise<UnitStack> {
        // const jsonUrl = `assets/units/${unitId}.json`;
        const pngUrl = resolveAsset(`@assets/units/${unitId}.png`);

        const config = UNITS_DATA[unitId as keyof typeof UNITS_DATA];

        const battleMap = useBattleStore.getState().battleMap;
        const texture = await PIXI.Assets.load(pngUrl);
        // const uniqueUnitId = `${unitId}_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
        
        const isRanged = (config.skills[0]?.range ?? 0) > 2;
        const startPoint = isRanged ? spawnZone.back : spawnZone.front;
        const finalPos = this.findFreeHex(startPoint.q, startPoint.r, battleMap);

        const hex = battleMap.get(coordsToId(finalPos));
        if (hex) hex.entityId = 'reserved';

        const pos = axialToPixel(finalPos.q, finalPos.r);
        // 1. СТВОРЮЄМО ЗАПИС У STORE

        useBattleStore.getState().addUnit({
            unitId: uniqueUnitId,
            unitName: config.unitName.id || config.unitName.message || '',
            count: count,
            maxUnitHp: config.stats.hp * count,
            moveCostMin: PathCalculator.getStepCost(config.stats.speed, TERRAINS[TerrainType.Plains].moveCost),
            currentUnitHp: config.stats.hp,
            currentStamina: config.stats.stamina,
            q: finalPos.q,
            r: finalPos.r,
            opportunityAttackUsed: false,
            isDead: false,
            faction: faction,
            stats: { ...config.stats },
            skills: config.skills.map(s => ({
                ...s,
                name: s.name.id || s.name.message || '',
            })),
            unitPortrait: config.unitPortrait || null
        });

        // 2. СТВОРЮЄМО ВІЗУАЛЬНИЙ КЛАС
        const unit = new UnitStack(texture, config, uniqueUnitId, faction, finalPos.q, finalPos.r, pos.x, pos.y);

        if (hex) {
            const registry = ManagerRegistry.getInstance().entityRegistry;
            const regHex = registry.getHex(hex.id);
            regHex?.occupy(unit.unitId);
        }

        return unit;
    }


    static findFreeHex(q: number, r: number, battleMap: Map<string, Hex>): { q: number, r: number } {
        
        const isBlocked = (q: number, r: number) => {
            const hex = battleMap.get(coordsToId({ q, r }));
            return !hex || hex.isObstacle || !!hex.entityId;
        };

        if (!isBlocked(q, r)) return { q, r };

        // Смещаем по R в обе стороны
        for (let d = 1; d <= 4; d++) {
            if (!isBlocked(q, r + d)) return { q, r: r + d };
            if (!isBlocked(q, r - d)) return { q, r: r - d };
        }

        // Fallback: q+1
        for (let d = 1; d <= 4; d++) {
            if (!isBlocked(q + 1, r + d)) return { q: q + 1, r: r + d };
            if (!isBlocked(q + 1, r - d)) return { q: q + 1, r: r - d };
        }

        // Ничего не нашли — возвращаем оригинал
        return { q, r };
    }
}